import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Icon, type IconName } from './Icon';

export interface MenuItem {
  id?: string;
  label?: React.ReactNode;
  icon?: IconName;
  danger?: boolean;
  separator?: boolean;
}

export interface MenuProps {
  items: MenuItem[];
  onSelect?: (id?: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

const Surface = styled.div`
  background: ${({ theme }) => theme.color.bg.default};
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.radius.base};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  padding: 4px;
  min-width: 180px;
`;
const Item = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  border: 0;
  background: none;
  width: 100%;
  text-align: left;
  color: ${({ theme, $danger }) => ($danger ? theme.color.danger : theme.color.fg.default)};
  &:hover { background: ${({ theme, $danger }) => ($danger ? theme.color.red[50] : theme.color.bg.subtle)}; }
  ${({ theme, $danger }) => css`& > svg { color: ${$danger ? theme.color.danger : theme.color.fg.subtle}; }`}
`;
const Sep = styled.div`height: 1px; background: ${({ theme }) => theme.color.border.default}; margin: 4px 0;`;

/** Lumen dropdown menu surface — position it yourself inside a relative container. */
export function Menu({ items, onSelect, className, style }: MenuProps) {
  return (
    <Surface className={className} style={style} role="menu">
      {items.map((it, i) =>
        it.separator ? (
          <Sep key={`sep-${i}`} />
        ) : (
          <Item key={it.id ?? i} role="menuitem" $danger={it.danger} onClick={() => onSelect?.(it.id)}>
            {it.icon && <Icon name={it.icon} size={14} />}
            {it.label}
          </Item>
        )
      )}
    </Surface>
  );
}
